/* istanbul ignore file */
import React from 'react';
import { ShowSection, SectionRow, BigSectionRow, ShowPageLayout } from 'framework/ui/layout';
import { TextField, DateField } from 'framework/ui/field';
import ExpenseHeader from 'domain/finance/expenses/shared/ExpenseHeader';
import SingleExpenseBreadcrumbs from 'domain/finance/expenses/shared/SingleExpenseBreadcrumbs';
import { InvoiceTotalField } from 'domain/shared/core/invoice';
import ExpenseStatusField from 'domain/finance/expenses/shared/field/ExpenseStatusField';
import ExpenseTypeField from 'domain/finance/expenses/shared/field/ExpenseTypeField';
import ExpenseSubTypeField from 'domain/finance/expenses/shared/field/ExpenseSubTypeField';
import MarkAsPaidExpenseButton from 'domain/finance/expenses/shared/button/MarkAsPaidExpenseButton';
import MarkAsPaidBonusButton from 'domain/finance/expenses/shared/button/MarkAsPaidBonusButton';
import RejectExpenseButton from 'domain/finance/expenses/shared/button/RejectExpenseButton';
import { EXPENSE_STATUSES, EXPENSE_SUBTYPES_ALIASES, EXPENSE_TYPES } from 'domain/shared/expense';
import { useExpenseId } from 'domain/finance/expenses/shared/useExpenseId';
import { BUSINESS_OBJECT_TYPES } from 'domain/finance/ledger/useLedgerService';
import TransfersListField from 'domain/finance/shared/field/TransfersListField';
import { LedgerLink } from 'domain/shared/ledger';
import { EmployeeLinkFieldWithNameLoading, OfficeCodeField } from 'domain/shared/employee';
import { DATE_FORMAT } from 'framework/utils';
import CancelExpenseButton from '../shared/button/CancelExpenseButton';
import { AccountLinkField } from 'domain/shared/account';
import { ProjectLinkField, READ_PROJECTS_LIST } from 'domain/shared/project';
import LeaveLink from 'domain/shared/leave/field/LeaveLink';

const isSalesBonus = (data: any) =>
  [
    EXPENSE_SUBTYPES_ALIASES[EXPENSE_TYPES.ONE_TIME_PAYMENT].SALES_BONUSES_AND_COMMISSIONS,
    EXPENSE_SUBTYPES_ALIASES[EXPENSE_TYPES.ONE_TIME_PAYMENT].ACCOUNT_MANAGER_BONUSES_AND_COMMISSIONS,
  ].includes(data.getValueBySource('subtype'));

const isCycledExpense = (data: any) =>
  data.getValueBySource('type') === EXPENSE_TYPES.PERFORMANCE_BASED_BONUS ||
  data.getValueBySource('type') === EXPENSE_TYPES.EMPLOYEE_EXPENSE_ACCRUAL;

const isAccountRelatedInfoRequired = (data: any) =>
  data.getValueBySource('type') === EXPENSE_TYPES.DEDUCTION ||
  data.getValueBySource('subtype') === EXPENSE_SUBTYPES_ALIASES[EXPENSE_TYPES.ONE_TIME_PAYMENT].REIMBURSEMENTS;

const ViewExpensePageContent = ({
  isSensitiveDataLoading = false,
  isSensitiveDataIsShown = false,
  toggleSensitiveDataButton = <></>,
}: {
  isSensitiveDataLoading?: boolean;
  isSensitiveDataIsShown?: boolean;
  toggleSensitiveDataButton?: React.ReactElement;
}) => {
  const id = useExpenseId();

  return (
    <ShowPageLayout
      entityHeader={<ExpenseHeader />}
      breadcrumbs={<SingleExpenseBreadcrumbs />}
      hasSeparateSections
      headerButtons={[
        toggleSensitiveDataButton,
        <MarkAsPaidExpenseButton
          ids={[id]}
          isVisible={(data: any) =>
            data.getValueBySource('status') === EXPENSE_STATUSES.PENDING && !isCycledExpense(data)
          }
        />,
        <MarkAsPaidBonusButton
          isVisible={(data: any) =>
            data.getValueBySource('status') === EXPENSE_STATUSES.COMPLETED && isCycledExpense(data)
          }
        />,
        <RejectExpenseButton
          ids={[id]}
          isVisible={(data: any) =>
            (data.getValueBySource('status') === EXPENSE_STATUSES.PENDING ||
              data.getValueBySource('status') === EXPENSE_STATUSES.REGISTERED) &&
            !isCycledExpense(data)
          }
        />,
        <CancelExpenseButton statusSource="status" />,
      ]}
    >
      <ShowSection title="Main information">
        <SectionRow>
          <TextField source="id" label="Id" />
          <ExpenseTypeField source="type" label="Type" />
          <ExpenseSubTypeField
            source="subtype"
            typeSource="type"
            isVisible={(data: any) =>
              [
                EXPENSE_TYPES.LEAVE_COMPENSATION,
                EXPENSE_TYPES.ONE_TIME_PAYMENT,
                EXPENSE_TYPES.BENEFITS_EXPENSE,
                EXPENSE_TYPES.TAX,
                EXPENSE_TYPES.DEDUCTION,
              ].includes(data.getValueBySource('type'))
            }
          />
          <OfficeCodeField source="employee.office" />
        </SectionRow>

        <SectionRow>
          <TextField source="registrationInfo.title" label="Title" />
          <InvoiceTotalField
            source="compensation.amount"
            currencySource="compensation.currency"
            label="Compensation"
            isVisible={isSensitiveDataIsShown}
          />
          <ExpenseStatusField source="status" label="Status" />
          <TextField
            label="Reject reason"
            source="rejectReason"
            isVisible={(data: any) => data.getValueBySource('status') === EXPENSE_STATUSES.REJECTED}
          />
          <TextField
            label="Cancel reason"
            source="rejectReason"
            isVisible={(data: any) => data.getValueBySource('status') === EXPENSE_STATUSES.CANCELLED}
          />
        </SectionRow>

        <SectionRow>
          <AccountLinkField
            label="Account"
            source="additionalInfo.project.accountId"
            idSource="additionalInfo.project.accountId"
            isInternal
            idValue={undefined}
            isVisible={isSalesBonus}
          />
          <TextField source="additionalInfo.project.sowId" label="SOW number" isVisible={isSalesBonus} />
          <ProjectLinkField
            source="additionalInfo.project.projectId"
            nameSource="additionalInfo.project.projectId"
            label="Project"
            isVisible={isSalesBonus}
            permissionToCheck={READ_PROJECTS_LIST}
          />
        </SectionRow>

        <SectionRow>
          <DateField source="registrationInfo.registrationDate" label="Registration date" />
          <DateField source="schedule.startDate" label="Start date" isVisible={isCycledExpense} />
          <DateField source="schedule.endDate" label="End date" isVisible={isCycledExpense} />
          <DateField
            source="schedule.latestAccountingCycle"
            label="Last accounting cycle"
            isVisible={isCycledExpense}
            dateFormat={DATE_FORMAT.MONTH}
          />
          <DateField
            source="payoutInfo.payoutDate"
            label="Payout date"
            isVisible={(data: any) => data.getValueBySource('status') === EXPENSE_STATUSES.PAID}
          />
          <EmployeeLinkFieldWithNameLoading
            source="employee.alias"
            label="Worker"
            isVisible={(data: any) => EXPENSE_TYPES.TAX !== data.getValueBySource('type')}
          />
          <LeaveLink
            source="additionalInfo.leaveId"
            nameSource="additionalInfo.leaveId"
            label="Leave"
            isVisible={(data: any) =>
              data.getValueBySource('additionalInfo')?.leaveId &&
              data.getValueBySource('type') === EXPENSE_TYPES.LEAVE_COMPENSATION
            }
          />
          <LedgerLink
            source="additionalInfo.transferAccount"
            nameSource="additionalInfo.transferAccount"
            label="Account to debit"
            isVisible={isAccountRelatedInfoRequired}
          />
          <TextField
            source="registrationInfo.budgetCategory"
            nameSource="registrationInfo.budgetCategory"
            label="Budget category"
            isVisible={isAccountRelatedInfoRequired}
          />
        </SectionRow>

        <BigSectionRow>
          <TextField source="registrationInfo.description" />
        </BigSectionRow>
      </ShowSection>
      <ShowSection isVisible={isSensitiveDataIsShown || isSensitiveDataLoading}>
        <TransfersListField
          businessObjectId={id}
          businessObjectType={BUSINESS_OBJECT_TYPES.EMPLOYEE_EXPENSE}
          startDateSource="registrationInfo.registrationDate"
          hasTransferIdField
        />
      </ShowSection>
    </ShowPageLayout>
  );
};

export default ViewExpensePageContent;
